import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LoaderService } from '../../shared/services/loader.service';
import { ApiService } from '../../shared/services/api.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ModalService } from '../../shared/services/modal.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-blogs-stories',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './blogs-stories.component.html',
  styleUrl: './blogs-stories.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogsStoriesComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private route: Router,
    private loaderService: LoaderService,
    private apiService: ApiService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
  ) { }

  @ViewChild('carouselTrack') carouselTrack!: ElementRef;
  @ViewChild('carouselContainer') carouselContainer!: ElementRef;

  // Carousel state
  currentSlide: number = 0;
  translateX: number = 0;
  itemWidth: number = 0;
  visibleItems: number = 3; // Default, will be calculated based on screen width
  totalSlides: number = 0;

  // Video modal state
  isVideoModalOpen: boolean = false;
  isVideoPlaying: boolean = true;
  showIndicator: boolean = false;
  private indicatorTimer: any;
  currentVideoUrl: string = '';
  currentVideoTitle: string = '';
  isYouTubeVideo: boolean = false;
  safeYouTubeUrl: SafeResourceUrl | null = null;
  private messageListener?: (event: MessageEvent) => void;
  private dataLoaded = false;
  slideWidth = 310;
  gap = 24; // assuming 6 (1.5rem = 24px)
  visibleSlides = 3; // Default for desktop

  // Removed duplicate declaration of visibleItems
  sub: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  // News items data
  newsItems: NewsItem[] = [];
  ngOnInit() {
    if (this.dataLoaded) return;
    this.dataLoaded = true;

    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    console.log('BlogsStoriesComponent ngOnInit fired');
    this.getBloglist();

    if (isPlatformBrowser(this.platformId)) {
      this.messageListener = (event: MessageEvent) => {
        try {
          const data =
            typeof event.data === 'string'
              ? JSON.parse(event.data)
              : event.data;
          if (data?.event === 'onStateChange') {
            if (data.info === 1) {
              this.isVideoPlaying = true;
              this.cdr.markForCheck();
            } else if (data.info === 2 || data.info === 0) {
              this.isVideoPlaying = false;
              this.cdr.markForCheck();
            }
          }
        } catch (_) {}
      };
      window.addEventListener('message', this.messageListener);
    }
  }
  ngAfterViewInit() {
    // Set up initial sizes only if elements are available
    if (this.carouselContainer?.nativeElement) {
      this.calculateLayout();
    }
    this.totalSlides = this.newsItems.length;

    // Add resize listener
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', this.onResize.bind(this));
    }
  }

  calculateLayout() {
    // Check if the carousel container element exists
    if (!this.carouselContainer?.nativeElement) {
      console.warn('Carousel container not available yet');
      return;
    }

    const containerWidth = this.carouselContainer.nativeElement.offsetWidth;
    const cardWidth = 310; // Base card width
    const cardMargin = 24; // 6 * 4px (mr-6)

    // Calculate how many items can fit in view
    this.visibleItems = Math.max(
      1,
      Math.floor(containerWidth / (cardWidth + cardMargin)),
    );

    // Update item width for calculations
    this.itemWidth = cardWidth + cardMargin;

    // If current position is now invalid after resize, adjust it
    if (this.currentSlide > this.newsItems.length - this.visibleItems) {
      this.currentSlide = Math.max(
        0,
        this.newsItems.length - this.visibleItems,
      );
      this.updateCarouselPosition();
    }

    this.cdr.markForCheck();
  }

  updateCarouselPosition() {
    if (this.itemWidth > 0) {
      this.translateX = -this.currentSlide * this.itemWidth;
      this.cdr.markForCheck();
    }
  }

  nextSlide() {
    if (this.currentSlide < this.totalSlides - this.visibleItems) {
      this.currentSlide++;
      this.updateCarouselPosition();
    } else {
      // this.notificationService.info('You have reached the end of the carousel');
    }
  }

  prevSlide() {
    if (this.currentSlide > 0) {
      this.currentSlide--;
      this.updateCarouselPosition();
    } else {
      // this.notificationService.info('You are at the beginning of the carousel');
    }
  }

  // Universal extractor for YouTube video ID
  extractYouTubeVideoId(url: string | undefined): string | null {
    if (!url) return null;
    const cleanUrl = url.trim();
    const regExp = /(?:youtube(?:-nocookie)?\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|shorts)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = cleanUrl.match(regExp);
    return match ? match[1] : null;
  }

  // Click handler for news items
  handleCardClick(newsItem: NewsItem) {
    const videoUrl = newsItem.youtube_url || newsItem.video_path || (newsItem as any).video_url;
    if (videoUrl && (newsItem.type_of_blog === 'video' || newsItem.video_path || newsItem.youtube_url)) {
      this.playVideo(videoUrl, newsItem.title || newsItem.author_name);
    } else {
      // Handle regular news item click (e.g., navigate to article page)
      console.log('Clicked on news item:', newsItem.author_name);
      // this.notificationService.info('Opening article...');
      const blogId =
        (newsItem as any).id ??
        (newsItem as any).blog_id ??
        (newsItem as any).row_id ??
        (newsItem as any)?._id;
      if (blogId) {
        this.route.navigate(['/blog-detail', blogId], {
          state: { blogItem: newsItem },
        });
      } else {
        this.goTo();
      }
    }
  }

  // Method to play video
  playVideo(videoUrl: string, title: string) {
    this.currentVideoUrl = videoUrl;
    this.currentVideoTitle = title;
    this.isVideoPlaying = true;
    const videoId = this.extractYouTubeVideoId(videoUrl);
    if (videoId) {
      this.isYouTubeVideo = true;
      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : '';
      const params = new URLSearchParams({
        autoplay: '1',
        controls: '1',
        playsinline: '1',
        rel: '0',
        modestbranding: '1',
        enablejsapi: '1',
        fs: '1',
      });
      if (origin) {
        params.set('origin', origin);
      }
      this.safeYouTubeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${videoId}?${params.toString()}`
      );
    } else {
      this.isYouTubeVideo = false;
      this.safeYouTubeUrl = null;
    }
    this.isVideoModalOpen = true;
    this.modalService.openModal('home-video', () => this.closeVideoModal());
    this.cdr.markForCheck();
  }

  showFeedbackIndicator(): void {
    this.showIndicator = true;
    if (this.indicatorTimer) {
      clearTimeout(this.indicatorTimer);
    }
    this.indicatorTimer = setTimeout(() => {
      this.showIndicator = false;
      this.cdr.markForCheck();
    }, 600);
  }

  togglePlayPause() {
    if (this.isYouTubeVideo) {
      const iframe = document.getElementById(
        'youtubeHomeVideoIframe',
      ) as HTMLIFrameElement;
      if (iframe?.contentWindow) {
        const command = this.isVideoPlaying ? 'pauseVideo' : 'playVideo';
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: command, args: '' }),
          '*',
        );
        this.isVideoPlaying = !this.isVideoPlaying;
        this.showFeedbackIndicator();
        this.cdr.markForCheck();
      } else {
        this.isVideoPlaying = !this.isVideoPlaying;
        this.showFeedbackIndicator();
        this.cdr.markForCheck();
      }
    } else {
      const videoEl = document.querySelector('video') as HTMLVideoElement;
      if (videoEl) {
        if (this.isVideoPlaying) {
          videoEl.pause();
          this.isVideoPlaying = false;
        } else {
          videoEl.play();
          this.isVideoPlaying = true;
        }
        this.showFeedbackIndicator();
        this.cdr.markForCheck();
      }
    }
  }

  // Method to close video modal
  closeVideoModal() {
    if (this.indicatorTimer) {
      clearTimeout(this.indicatorTimer);
    }
    this.showIndicator = false;
    if (this.isYouTubeVideo) {
      const iframe = document.getElementById(
        'youtubeHomeVideoIframe',
      ) as HTMLIFrameElement;
      if (iframe?.contentWindow) {
        try {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
            '*',
          );
        } catch (_) {}
      }
    }
    this.isVideoModalOpen = false;
    this.modalService.closeModal('home-video');
    this.safeYouTubeUrl = null;
    this.isVideoPlaying = false;
    this.cdr.markForCheck();
  }

  goTo() {
    this.route.navigateByUrl('/blog-stories');
  }

  getBloglist(): void {
    let screenType = 'Desktop';

      if (typeof window !== 'undefined' && window.innerWidth < 640) {
        screenType = 'Mobile';
      } else if (typeof window !== 'undefined' && window.innerWidth >= 640 && window.innerWidth < 1024) {
        screenType = 'Tablet';
      } else {
        screenType = 'Desktop';
      }
      const payload = { action_type: 'Get Blogs', image_type: screenType };
      this.loaderService.show();

      this.apiService
        .getBlogs(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.loaderService.hide();
            if (res.success) {
              this.newsItems = res.data;
              this.totalSlides = this.newsItems.length;

              // if (this.newsItems.length > 0) {
              //   // this.notificationService.success(`${this.newsItems.length} blog(s) loaded successfully`);
              // } else {
              //   this.notificationService.info('No blogs available at the moment');
              // }

              // Recalculate layout after data is loaded and view is updated
              setTimeout(() => {
                this.calculateLayout();
              }, 0);

              this.cdr.markForCheck();
            } else {
              console.warn('Blogs API returned success: false');
              this.newsItems = [];
              // this.notificationService.error('Failed to load blogs');
              this.cdr.markForCheck();
            }
          },
          error: (err) => {
            this.loaderService.hide();
            console.error('Failed to fetch blog list', err);

            // Handle specific error codes with user-friendly messages
            // if (err.status === 412) {
            //   this.notificationService.error(
            //     'Server temporarily unavailable. Please try again later.',
            //   );
            // } else if (err.status === 401) {
            //   this.notificationService.error('Please log in to view blogs');
            // } else if (err.status === 403) {
            //   this.notificationService.error(
            //     'Access denied. Please contact support.',
            //   );
            // } else if (err.status === 404) {
            //   this.notificationService.error('Blogs not found');
            // } else if (err.status === 500) {
            //   this.notificationService.error(
            //     'Server error. Please try again later.',
            //   );
            // } else {
            //   this.notificationService.error(
            //     err?.error?.data?.msg ||
            //       'Failed to load blogs. Please try again.',
            //   );
            // }

            this.newsItems = [];
            this.cdr.markForCheck();
          },
        });
    }

    ngOnDestroy() {
      if (this.indicatorTimer) {
        clearTimeout(this.indicatorTimer);
      }
      if (this.messageListener && isPlatformBrowser(this.platformId)) {
        window.removeEventListener('message', this.messageListener);
      }
      // Clean up all subscriptions
      this.modalService.closeModal('home-video');
      this.destroy$.next();
      this.destroy$.complete();

      if (this.sub) {
        this.sub.unsubscribe();
      }

      // Remove resize listener
      if (isPlatformBrowser(this.platformId)) {
        window.removeEventListener('resize', this.onResize.bind(this));
      }
    }

  private onResize() {
    this.calculateLayout();
    this.updateCarouselPosition();
  }
}

export interface NewsItem {
  type_of_blog: 'blog' | 'video';
  author_description: string;
  images: string[];
  description: string;
  created_date: string;
  author_name: string;
  video_path: string;
  youtube_url: string;
  title: string;
}
